/**
 * The cave cover (04 §4) in miniature: when a menu's cave picture changes, shimmering cover tiles
 * hide it and dissolve at random, as a cave uncovers before play.
 */

import { atlasFor } from '../../render/atlas';
import type { Screen } from '../../render/screen';
import { hash } from '../../render/sprites/types';
import type { Rect } from './menuModel';
import { animFrame } from './theme';

export const UNCOVER_MS = 420;

/** Whether cell `i` is still covered `since` ms into the uncover (seeded per change). Pure. */
export function stillCovered(i: number, seed: number, since: number): boolean {
  if (since >= UNCOVER_MS) return false;
  if (since < 0) return true;
  return (hash(i, seed, 11) % 1000) / 1000 >= since / UNCOVER_MS;
}

/** Cover tiles over `r`, `cell` pixels each (16 or 8), fading out over UNCOVER_MS. */
export function drawUncover(
  screen: Screen, r: Rect, since: number, seed: number, colors: readonly [number, number, number], cell = 16,
): void {
  if (since >= UNCOVER_MS) return;
  const atlas = atlasFor(colors);
  const ctx = screen.ctx;
  const cols = Math.ceil(r.w / cell);
  const rows = Math.ceil(r.h / cell);
  const f = animFrame(since);
  ctx.save();
  ctx.beginPath();
  ctx.rect(r.x, r.y, r.w, r.h);
  ctx.clip();
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (!stillCovered(y * cols + x, seed, since)) continue;
      if (cell === 16) atlas.draw(ctx, 'cover', f + x + y, r.x + x * cell, r.y + y * cell);
      else {
        ctx.save();
        ctx.translate(r.x + x * cell, r.y + y * cell);
        ctx.scale(cell / 16, cell / 16);
        atlas.draw(ctx, 'cover', f + x + y, 0, 0);
        ctx.restore();
      }
    }
  }
  ctx.restore();
}
